import React, { FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Loader } from '@components';
import Button from '@components/ButtonTs';
import InfiniteList from '@components/InfiniteList';
import Input from '@components/Input';
import OrderStatus from '@components/CRM/Orders/OrderStatus';
import { ArrowRight } from '@icons/common';
import { useDebounce } from '@hooks/index';

import ClientAvatar from './ClientAvatar';
import { B2BClient } from './types';
import {
  fetchB2BClientsAction,
  selectB2BClientAction,
  updateB2BClientAction,
} from '../../../store/actions/b2bClients';
import {
  clearActiveClient,
  setFilters,
} from '../../../store/reducers/b2bClients';
import {
  activeB2BClientIdSelector,
  activeB2BClientSelector,
  b2bClientsErrorSelector,
  b2bClientsFiltersSelector,
  b2bClientsMetaSelector,
  b2bClientsSelector,
  filteredB2BClientsSelector,
  isB2BActiveClientLoadingSelector,
  isB2BClientSavingSelector,
  isB2BClientsLoadingSelector,
} from '../../../store/selectors/b2bClients';

export const AGENT_CLIENTS_ROUTE = '/clients';
export const AGENT_CLIENTS_PATH = `${AGENT_CLIENTS_ROUTE}/:client_id?`;

const CLIENT_ROW_HEIGHT = 80;
const INFINITE_LIST_THRESHOLD = 3;

const ClientsPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { client_id: clientIdFromUrl } = useParams<{ client_id?: string }>();
  const clients = useSelector(b2bClientsSelector);
  const filteredClients = useSelector(filteredB2BClientsSelector);
  const selectedClientId = useSelector(activeB2BClientIdSelector);
  const selectedClient = useSelector(activeB2BClientSelector);
  const { query } = useSelector(b2bClientsFiltersSelector);
  const meta = useSelector(b2bClientsMetaSelector);
  const error = useSelector(b2bClientsErrorSelector);
  const isLoading = useSelector(isB2BClientsLoadingSelector);
  const isLoadingActiveClient = useSelector(
    isB2BActiveClientLoadingSelector
  );

  const debouncedQuery = useDebounce(query, 350);
  const normalizedQuery = debouncedQuery.trim();
  const hasNextPage = filteredClients.length < meta.total;

  useEffect(() => {
    dispatch(
      fetchB2BClientsAction({
        query: normalizedQuery,
        page: 1,
        perPage: meta.perPage,
      })
    );
  }, [dispatch, meta.perPage, normalizedQuery]);

  useEffect(() => {
    if (!clientIdFromUrl) {
      if (selectedClientId || selectedClient || isLoadingActiveClient) {
        dispatch(clearActiveClient());
      }

      return;
    }

    if (selectedClientId !== clientIdFromUrl) {
      dispatch(selectB2BClientAction({ id: clientIdFromUrl }));
    }
  }, [
    clientIdFromUrl,
    dispatch,
    isLoadingActiveClient,
    selectedClient,
    selectedClientId,
  ]);

  const handleSelectClient = (id: string) => {
    if (id === selectedClientId) return;

    history.push(`${AGENT_CLIENTS_ROUTE}/${id}`);
  };

  const handleLoadMore = () => {
    if (isLoading || !hasNextPage) return;

    dispatch(
      fetchB2BClientsAction({
        query: normalizedQuery,
        page: meta.page + 1,
        perPage: meta.perPage,
      })
    );
  };

  return (
    <Page>
      <Topbar>
        <div>
          <Title>Клиенты</Title>
          <Muted>{meta.total} клиентов в базе агентства</Muted>
        </div>
      </Topbar>

      <Content>
        <ClientList>
          <ListSearch
            aria-label="Поиск клиентов"
            placeholder="Имя, телефон или email…"
            value={query}
            onChange={(event) =>
              dispatch(setFilters({ query: event.target.value }))
            }
          />
          <ListBody>
            {error && <ListError>{error}</ListError>}
            {isLoading && !clients.length ? (
              <ListLoader />
            ) : filteredClients.length ? (
              <InfiniteList
                key={normalizedQuery || 'all-clients'}
                isItemLoaded={(index) => index < filteredClients.length}
                isNextPageLoading={isLoading}
                itemCount={
                  hasNextPage
                    ? filteredClients.length + 1
                    : filteredClients.length
                }
                itemData={filteredClients}
                itemKey={(index, data) =>
                  data[index]?.id || `client-loader-${index}`
                }
                itemSize={CLIENT_ROW_HEIGHT}
                minimumBatchSize={meta.perPage}
                onLoadRequest={handleLoadMore}
                threshold={INFINITE_LIST_THRESHOLD}
              >
                {({ data, index, style }) => {
                  const client = data[index];

                  if (!client) {
                    return (
                      <NextPageLoader style={style}>
                        {isLoading && <Loader size={20} />}
                      </NextPageLoader>
                    );
                  }

                  return (
                    <ClientRow
                      key={client.id}
                      style={style}
                      type="button"
                      $active={client.id === selectedClientId}
                      onClick={() => handleSelectClient(client.id)}
                    >
                      <ListAvatar client={client} />
                      <ClientRowInfo>
                        <strong>{client.fullName}</strong>
                        <span>{client.phone}</span>
                        <small>{client.email}</small>
                      </ClientRowInfo>
                      <StyledArrowRight />
                    </ClientRow>
                  );
                }}
              </InfiniteList>
            ) : (
              <EmptyList>
                <strong>Клиенты не найдены</strong>
                <Muted>Измените поисковый запрос</Muted>
              </EmptyList>
            )}
          </ListBody>
        </ClientList>

        <DetailsPanel>
          {isLoadingActiveClient ? (
            <DetailsLoader />
          ) : selectedClient ? (
            <ClientDetails key={selectedClient.id} client={selectedClient} />
          ) : (
            <EmptyDetails>
              <strong>Выберите клиента</strong>
              <Muted>
                Карточка объединяет контакты, туристов и заказы.
              </Muted>
            </EmptyDetails>
          )}
        </DetailsPanel>
      </Content>
    </Page>
  );
};

const ClientDetails: React.FC<{ client: B2BClient }> = ({ client }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isSaving = useSelector(isB2BClientSavingSelector);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    name: client.name,
    surname: client.surname,
    email: client.email,
    phone: client.phone,
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    dispatch(updateB2BClientAction({ id: client.id, client: form }));
    setIsEditing(false);
  };

  return (
    <Details>
      <ClientHeader>
        <HeaderAvatar client={client} />
        <div>
          <Muted>Клиент №{client.id}</Muted>
          <h2>{client.fullName}</h2>
        </div>
        <EditClientButton
          variant="default"
          size="tiny"
          onClick={() => setIsEditing((value) => !value)}
        >
          {isEditing ? 'Отмена' : 'Редактировать'}
        </EditClientButton>
      </ClientHeader>

      <DetailGrid>
        <MainColumn>
          <Block>
            <BlockTitle>Контактные данные</BlockTitle>
            {isEditing ? (
              <EditForm onSubmit={handleSubmit}>
                <EditInput
                  aria-label="Фамилия"
                  placeholder="Фамилия"
                  value={form.surname}
                  onChange={(event) =>
                    setForm({ ...form, surname: event.target.value })
                  }
                />
                <EditInput
                  aria-label="Имя"
                  placeholder="Имя"
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
                <EditInput
                  aria-label="Телефон"
                  placeholder="Телефон"
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                />
                <EditInput
                  aria-label="Email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(event) =>
                    setForm({ ...form, email: event.target.value })
                  }
                />
                <SaveButton
                  disabled={isSaving}
                  variant="primary"
                  size="tiny"
                  type="submit"
                >
                  {isSaving ? 'Сохраняем…' : 'Сохранить'}
                </SaveButton>
              </EditForm>
            ) : (
              <InfoGrid>
                <Info>
                  <span>Телефон</span>
                  <p>{client.phone || '—'}</p>
                  {client.unconfirmedPhone && (
                    <Pending>
                      {client.unconfirmedPhone} · ждёт подтверждения
                    </Pending>
                  )}
                </Info>
                <Info>
                  <span>Email</span>
                  <p>{client.email || '—'}</p>
                  {client.unconfirmedEmail && (
                    <Pending>
                      {client.unconfirmedEmail} · ждёт подтверждения
                    </Pending>
                  )}
                </Info>
                <Info>
                  <span>В базе с</span>
                  <p>{client.createdAt}</p>
                </Info>
              </InfoGrid>
            )}
          </Block>
        </MainColumn>

        <SideColumn>
          <Block>
            <BlockTitle>Заказы</BlockTitle>
            <Relations>
              {client.orders.length ? (
                client.orders.map((order) => (
                  <Relation
                    key={order.id}
                    type="button"
                    onClick={() =>
                      history.push(`/orders/${order.id}`)
                    }
                  >
                    <RelationInfo>
                      <p>
                        №{order.id} · {order.hotelName}
                      </p>
                      <span>{order.destination}</span>
                    </RelationInfo>
                    <CompactOrderStatus status={order.status} />
                    <RelationArrow />
                  </Relation>
                ))
              ) : (
                <RelationEmpty>Заказов пока нет</RelationEmpty>
              )}
            </Relations>
          </Block>
        </SideColumn>
      </DetailGrid>
    </Details>
  );
};

const Panel = styled.section`
  background: #fff;
  border: 1px solid #dfe2e6;
  border-radius: 7px;
  box-shadow: 0 1px 4px rgba(24, 35, 48, 0.05);
`;

const Page = styled.main`
  align-self: stretch;
  background: #eef0f2;
  display: grid;
  gap: 12px;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 650px;
  padding: 12px;
  width: 100%;
`;

const Topbar = styled(Panel)`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;

  > div:first-child {
    display: grid;
    gap: 3px;
  }
`;

const Title = styled.h1`
  font-size: 21px;
  line-height: 26px;
  margin: 0;
`;

const Muted = styled.div`
  color: #919499;
  font-size: 12px;
  line-height: 17px;
`;

const Content = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 330px minmax(650px, 1fr);
  min-height: 0;
`;

const ClientList = styled(Panel)`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
`;

const ListSearch = styled(Input)`
  border-color: #d9dce1;
  font-size: 13px;
  height: 36px;
  margin: 12px;
  padding: 0 10px;
  width: calc(100% - 24px);

  &:focus {
    border-color: #007aff;
    outline: none;
  }
`;

const ListBody = styled.div`
  border-top: 1px solid #e1e3e6;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const ListError = styled.div`
  background: #fff1f0;
  color: #b42318;
  font-size: 12px;
  left: 0;
  padding: 10px 12px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
`;

const NextPageLoader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const ClientRow = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active }) => ($active ? '#f1f7ff' : '#fff')};
  border: 0;
  border-bottom: 1px solid #eceef0;
  border-left: 3px solid
    ${({ $active }) => ($active ? '#007aff' : 'transparent')};
  cursor: pointer;
  display: grid;
  font-family: inherit;
  gap: 10px;
  grid-template-columns: 38px minmax(0, 1fr) 18px;
  padding: 12px;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({ $active }) => ($active ? '#f1f7ff' : '#f6f8fa')};
  }
`;

const ListAvatar = styled(ClientAvatar)`
  height: 38px;
  width: 38px;
`;

const ClientRowInfo = styled.div`
  display: grid;
  gap: 3px;
  min-width: 0;

  strong,
  span,
  small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span,
  small {
    color: #919499;
  }
`;

const StyledArrowRight = styled(ArrowRight)`
  color: #a3a6aa;
  height: 17px;
  width: 17px;
`;

const EmptyList = styled.div`
  display: grid;
  gap: 5px;
  justify-items: center;
  padding: 44px 20px;
  text-align: center;
`;

const ListLoader = styled(Loader)`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 220px;
  width: 100%;
`;

const DetailsPanel = styled(Panel)`
  min-width: 0;
  overflow: hidden;
`;

const DetailsLoader = styled(Loader)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 420px;
  width: 100%;
`;

const EmptyDetails = styled.div`
  align-content: center;
  display: grid;
  gap: 7px;
  height: 100%;
  justify-items: center;
  padding: 30px;
  text-align: center;
`;

const Details = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
`;

const ClientHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid #e1e3e6;
  display: grid;
  gap: 13px;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  padding: 16px 19px;

  h2 {
    font-size: 20px;
    margin: 2px 0 6px;
  }
`;

const HeaderAvatar = styled(ClientAvatar)`
  height: 58px;
  width: 58px;
`;

const EditClientButton = styled(Button)`
  width: 120px;
`;

const DetailGrid = styled.div`
  background: #f0f1f2;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(390px, 1.2fr) minmax(300px, 0.8fr);
  min-height: 0;
  overflow: auto;
  padding: 12px;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Block = styled.section`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const BlockTitle = styled.h3`
  border-bottom: 1px solid #eceef0;
  font-size: 15px;
  margin: 0;
  padding: 13px 14px;
`;

const EditForm = styled.form`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  padding: 14px;
`;

const EditInput = styled(Input)`
  border-color: #d9dce1;
  font-size: 13px;
  height: 36px;
  padding: 0 10px;
  width: 100%;
`;

const SaveButton = styled(Button)`
  border: none;
  grid-column: 2;
  justify-self: end;
  width: 120px;
`;

const InfoGrid = styled.div`
  background: #eceef0;
  display: grid;
  gap: 1px;
  grid-template-columns: 1fr 1fr;
`;

const Info = styled.div`
  background: #fff;
  display: grid;
  gap: 5px;
  padding: 14px;

  span {
    color: #919499;
    font-size: 12px;
  }
`;

const Pending = styled.small`
  color: #956600;
  font-size: 11px;
`;

const Relations = styled.div`
  display: grid;
`;

const Relation = styled.button`
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #edf0f2;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  gap: 10px;
  justify-content: space-between;
  padding: 12px 14px;
  text-align: left;
  width: 100%;

  &:hover {
    background: #f7f9fb;
  }
`;

const RelationInfo = styled.div`
  display: grid;
  flex: 1;
  gap: 4px;
  min-width: 0;

  p,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #919499;
    font-size: 11px;
  }
`;

// Как у карточки клиента на странице заказа: показывает, что по клику можно перейти к заказу.
const RelationArrow = styled(ArrowRight)`
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const CompactOrderStatus = styled(OrderStatus)`
  flex-shrink: 0;
  font-size: 11px;
  height: 21px;
  line-height: 21px;
  margin: 0;
  min-width: auto;
`;

const RelationEmpty = styled.div`
  color: #919499;
  padding: 24px 14px;
  text-align: center;
`;

export default ClientsPage;
